import axios from "axios";
import React, { useEffect, useState } from "react";
import { Alert, Form, Button, Modal } from "react-bootstrap";
import { activityCenterObject } from "../../../fileToolbar/ActivityCenter/activityCenterTypes";
import { formatAMPMString } from "../../../fileToolbar/fileToolbarFuncs";
import { displayToastMsg } from "../../toastMessages/toastmessages";

interface ThirdPartyUploadProps {
  documentName: string;
  posting: string;
  sid: string;
  msgID: string;
  isUploaded: boolean;
  requestRemove: boolean;
}

const ThirdPartyUpload = ({
  isUploaded,
  documentName,
  posting,
  sid,
  msgID,
  requestRemove,
}: ThirdPartyUploadProps) => {
  const [uploadSuccess, setUploadSuccess] = useState<boolean | null>(isUploaded === true ? true : null);
  const [showOverWrite, setShowOverWrite] = useState<boolean>(false);
  const [showFileTypeError, setShowFileTypeError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [tempRequestRemove, setTempRequestRemove] = useState<boolean>(requestRemove);
  useEffect(() => {
    setUploadSuccess(isUploaded === true ? true : null);
  }, [isUploaded]);

  // Same cap as MyDrive and the DocSigningAPI express-fileupload limit.
  const MAX_FILE_SIZE = 25 * Math.pow(2, 20); // 25 MB
  const GENERIC_UPLOAD_ERROR = "Your document upload was unsuccessful. Please try again.";
  const sizeLimitMessage = (fileName: string) =>
    `"${fileName}" exceeds the upload limit of 25MB. Please try another file.`;

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setShowFileTypeError(false);
    setErrorMessage("");
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.type !== "application/pdf") {
      setShowFileTypeError(true);
      return;
    }
    // Client-side size check so oversize files never leave the browser.
    if (selectedFile.size > MAX_FILE_SIZE) {
      event.target.value = "";
      setErrorMessage(sizeLimitMessage(selectedFile.name));
      return;
    }

    // Upload as multipart so the server receives the raw PDF bytes
    // (no base64 inflation).
    const formData = new FormData();
    formData.append("file", selectedFile);
    try {
      await axios.post(
        `/docsigning/info/uploadthirdparty/${msgID}?SessionID=${sid}`,
        formData,
        {
          params: {
            filename: documentName,
            postPackage: posting,
          },
        }
      );
      setUploadSuccess(true);
    } catch (error) {
      console.error("Error uploading file:", error);
      event.target.value = "";
      // A server-side size rejection shows the same message as the
      // client-side check; anything else shows the server text or a fallback.
      setErrorMessage(
        error?.response?.status === 413
          ? sizeLimitMessage(selectedFile.name)
          : error?.response?.data?.error || GENERIC_UPLOAD_ERROR
      );
      setUploadSuccess(null);
    }
  };

  useEffect(() => {
    if (uploadSuccess === true && isUploaded === false) {
      const timer = setTimeout(() => {
        setShowOverWrite(false);
      }, 2 * 60 * 60 * 1000); // 2 hours in milliseconds

      return () => clearTimeout(timer); // Cleanup the timer on component unmount
    }
  }, [uploadSuccess, isUploaded]);
  const handleRemove = async () => {
    try {
        const baseURL = window.location.origin;
        await axios.post(`${baseURL}/docsigning/info/inbox/delete/${msgID}?FileName=${documentName + '.pdf'}&postPackage=${posting}&SessionID=${sid}`);
        setUploadSuccess(null);
    } catch (error) {
        console.error("Error deleting the document:", error);
    }
}
const handleRequestRemove = async () => {
  const baseURL = window.location.origin;
  const response = await axios.post(`${baseURL}/docsigning/info/inbox/requestremove/${msgID}?SessionID=${sid}`, {
    fileName: documentName,
  });
  setShowModal(false);
  const newMessage: activityCenterObject = {
    index: undefined,
    message: response.status === 200 ? <p>Request to remove document sent.</p> : <p>Error sending request to remove document.</p>,
    type: response.status === 200 ? "success" : "error",
    dateCreated: null,
    clearWhenOut: true,
};
displayToastMsg({ id: null, msg: newMessage.message as JSX.Element, type: newMessage.type }, null);
setTempRequestRemove(true);
}
  
  const handleDownloadClick = async () => {
    const baseURL = window.location.origin;
    const response = await axios.get(
      `${baseURL}/docsigning/info/inbox/download/${msgID}?SessionID=${sid}`,
      {
        params: {
          filename: documentName,
          thirdpartyIndicator: true,
          postPackage: posting,
        },
        responseType: "arraybuffer",
      }
    );
    const pdfData = response.data;

    const blob = new Blob([pdfData], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = documentName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    function handleFileTypeError() {
      let currentDate = new Date();
      let dateStr = currentDate.toDateString().split(" ");
      let timeStr = currentDate.toTimeString();
      timeStr = formatAMPMString(timeStr);
      const newMessage: activityCenterObject = {
          index: undefined,
          message: <p>Please upload a <b>PDF</b> file.</p>,
          type: "error",
          dateCreated: timeStr + ' ' + dateStr[1] + ' ' + dateStr[2] + ' ',
          clearWhenOut: true,
      };
      displayToastMsg({ id: null, msg: newMessage.message as JSX.Element, type: newMessage.type }, null);
    }

    if (showFileTypeError) {
      handleFileTypeError();
    }
  }, [showFileTypeError]);

  return (
    <>
        {(isUploaded === false && uploadSuccess === null) && (
          <Form style={{height:'100%'}}>
            <Form.Group className="file-upload file-upload-default">
                <Form.Control 
                    type="file"
                    id={`upload-button-${documentName}`}
                    onChange={handleFileChange} 
                />
                <div className="file-upload-inner">
                    {errorMessage > "" && (
                      <Alert variant="danger">{errorMessage}</Alert>
                    )}
                    <i className="fa-duotone fa-upload" style={{fontSize: '28px'}}></i>
                    <h2 className="mb-2">{documentName}</h2>
                    <p>Drag and drop <br />a document here or</p>
                    <Button type="submit" variant="primary">Select File</Button>
                </div>
            </Form.Group>
          </Form>
        )}

        {showOverWrite && (
          <Form>
            <Form.Group>
              <Form.Control
                type="file"
                className="form-upload"
                id={`upload-button-${documentName}`}
                onChange={handleFileChange}
              />
              {/* Removed the upload button */}
            </Form.Group>
          </Form>
        )}
        
        {(isUploaded === true || uploadSuccess === true) && (
            <div className={"file-upload " + (!tempRequestRemove ? "file-upload-success" : "file-upload-warning")}>
                <div className="file-upload-inner">
                    <i className={"fa-solid " + (tempRequestRemove ? 'fa-trash-can-clock status-warning' : 'fa-memo-circle-check status-success')} style={{fontSize: '28px'}}></i>

                    <h3 className="mb-2">{documentName}</h3>
                    
                    {tempRequestRemove ?
                        <p>A removal request has been<br /> sent for this document.</p>
                        :
                        <p>Document Uploaded</p>
                    }

                    {uploadSuccess === true && isUploaded === false ? 
                        <>
                            <Button variant="success" className="mb-1" onClick={handleDownloadClick}>
                                <i className="fa-regular fa-eye status-success"></i>
                                Preview
                            </Button>

                            <br />

                            <Button variant="link-success" onClick={handleRemove}>
                                <i className="fa-regular fa-trash-can status-success"></i>
                                Remove
                            </Button>
                        </>

                        :

                        <>
                            <Button variant={(!tempRequestRemove ? "success" : "warning")} className="mb-1" onClick={handleDownloadClick}>
                                <i className="fa-regular fa-eye status-success"></i>
                                Preview
                            </Button>

                            {!tempRequestRemove &&
                                <>
                                    <br />

                                    <Button variant="link-success" onClick={() => setShowModal(true)}>
                                        <i className="fa-regular fa-trash-can-clock status-success"></i>
                                        Request Removal
                                    </Button>
                                </>
                            }
                        </>
                    }
                </div>
            </div>
        )}

    <Modal show={showModal} onHide={() => setShowModal(false)} centered>
    <Modal.Header closeButton>
      <h2>Confirm Removal</h2>
    </Modal.Header>
    <Modal.Body>
      Are you sure you want to remove the uploaded document?
    </Modal.Body>
    <Modal.Footer>
      <Button variant="outline-primary" onClick={() => setShowModal(false)}>
        Cancel
      </Button>
      <Button onClick={handleRequestRemove}>
        Confirm
      </Button>
    </Modal.Footer>
  </Modal>
    </>
  );
  
};

export default ThirdPartyUpload;
